import React from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';

import { expValEquals } from '@confluence/feature-experiments';
import { UserAndGroupSearchPicker } from '@confluence/user-and-group-search/entry-points/UserAndGroupSearchPicker.ts';
import type {
	OnOption,
	OptionData,
} from '@confluence/user-and-group-search/entry-points/user-picker.ts';
import type { UserGroupItemProps } from '@confluence/user-and-group-search/entry-points/UserAndGroupSearchPicker.ts';

import { useContentOwnershipContributors } from './useContentOwnershipContributors.ts';
import type { Owner } from './ownership-models.ts';

const userPickerContainerStyles = xcss({
	marginTop: 'space.050',
	marginBottom: 'space.250',
});

/**
 * Required to generate the ref used to attach UserPicker's menu onto
 * Copied from next/packages/restrictions/src/RestrictionsDialog/RestrictionsDialogComponent.tsx
 */
const UserPickerRefContainer = ({
	children,
}: {
	children: (refSt: HTMLElement | null) => JSX.Element | null;
}) => {
	const ref = React.useRef<HTMLElement | null>(null);
	const [refSt, setRefSt] = React.useState<HTMLElement | null>(null);

	return (
		<div
			ref={(current) => {
				ref.current = current;
				setRefSt(ref.current);
			}}
		>
			{children(refSt)}
		</div>
	);
};

export type PageOwnershipDialogUserPickerProps = {
	selectedUser: OptionData | undefined;
	onSelectUser: OnOption;
	onClearSelectedUser: () => void;
	spaceKey: string;
	contentId: string;
	currentOwner: Owner | undefined;
	ariaLabel?: string;
};

export const ContentOwnershipDialogUserPicker = ({
	selectedUser,
	onSelectUser,
	onClearSelectedUser,
	spaceKey,
	contentId,
	currentOwner,
	ariaLabel,
}: PageOwnershipDialogUserPickerProps) => {
	const bootstrapOptions = useContentOwnershipContributors({
		contentId,
		spaceKey,
	});

	// filter user dropdown results to exclude the current page owner
	const userResultsFilter = (values: UserGroupItemProps[]) => {
		const currentOwnerId = currentOwner?.id;

		return currentOwnerId ? values.filter((value) => value.id !== currentOwnerId) : values;
	};

	return (
		// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
		<Box xcss={userPickerContainerStyles}>
			<UserPickerRefContainer>
				{(ref) => (
					<UserAndGroupSearchPicker
						fieldId="content.owner"
						ariaLabel={
							expValEquals('a11y-fixes-week2-may-2026', 'isEnabled', true) ? ariaLabel : undefined
						}
						value={selectedUser}
						onSelect={onSelectUser}
						onClear={onClearSelectedUser}
						width="100%"
						isMulti={false}
						includeGroup={false}
						smart
						isClearable
						menuPortalTarget={ref || undefined}
						bootstrapOptions={bootstrapOptions}
						smartFilterOptions={userResultsFilter}
						styles={{
							menuPortal: (css) => ({
								...css,
								position: 'absolute',
								// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage/preview
								left: 'inherit', // override positional attrs
								// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage/preview
								top: 'inherit',
							}),
						}}
						noOptionsMessage={null}
					/>
				)}
			</UserPickerRefContainer>
		</Box>
	);
};
